'use client';

import React, { useState, useEffect } from 'react';
import { X, FileText, Loader2, Lock, Zap, CreditCard } from 'lucide-react';

type SubscriberStatus = null | 'non-professional' | 'professional';

interface CheckoutConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    subscriberStatus: 'non-professional' | 'professional',
    fullName: string,
    chosenTrial: boolean
  ) => void;
  onSwitchToElite: () => void;
  planName: string;
  /** Initial trial-vs-subscribe default when the modal opens. */
  isTrial: boolean;
  /** When true, renders the trial vs subscribe-today selector. */
  showTrialChoice?: boolean;
  /** Display price for the chosen plan, e.g. "$50/mo" or "$500/yr". */
  planPriceLabel?: string;
  userName: string | null;
  loading: boolean;
  isElitePlan: boolean;
}

export const CheckoutConfirmation: React.FC<CheckoutConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onSwitchToElite,
  planName,
  isTrial,
  showTrialChoice = false,
  planPriceLabel = '',
  userName,
  loading,
  isElitePlan,
}) => {
  const [fullName, setFullName] = useState('');
  const [status, setStatus] = useState<SubscriberStatus>('non-professional');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [chosenTrial, setChosenTrial] = useState(isTrial);

  useEffect(() => {
    setFullName(userName ?? '');
  }, [userName, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setStatus('non-professional');
      setTermsAccepted(false);
      setChosenTrial(isTrial);
    }
  }, [isOpen, isTrial]);

  if (!isOpen) return null;

  const nameValid = fullName.trim().length >= 2;
  const isProfessional = status === 'professional';
  const statusOk = status === 'non-professional' || (isProfessional && isElitePlan);
  const canProceed = statusOk && termsAccepted && nameValid && !loading;

  const planLabel = planName.charAt(0).toUpperCase() + planName.slice(1);
  const effectiveTrial = showTrialChoice ? chosenTrial : isTrial;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md animate-in fade-in-0 duration-200"
      onClick={(e) => { if (!loading) onClose(); e.stopPropagation(); }}
    >
      <div
        className="relative w-full max-w-lg mx-4 bg-zinc-950 border border-zinc-800/60 rounded-xl shadow-2xl shadow-black/50 overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cyan hairline along the top edge */}
        <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

        {/* Header */}
        <div className="px-6 pt-5 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white tracking-tight">
                {effectiveTrial ? `Start ${planLabel} Free Trial` : `Subscribe to ${planLabel}`}
              </h2>
              <p className="text-[11px] text-zinc-300 mt-0.5">Complete verification to continue</p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-zinc-400 hover:text-zinc-100 p-1 rounded-md transition-colors disabled:opacity-50 -mr-1"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 pb-5 space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">

          {/* ---- Trial vs Subscribe choice ---- */}
          {showTrialChoice && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setChosenTrial(true)}
                disabled={loading}
                className={`relative px-4 py-3.5 rounded-lg border text-left transition-all duration-150 ${
                  chosenTrial
                    ? 'bg-cyan-500/[0.08] border-cyan-400/50 shadow-lg shadow-cyan-500/10'
                    : 'bg-zinc-900/30 border-zinc-800/60 hover:border-zinc-600'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <Zap size={14} className={chosenTrial ? 'text-cyan-300' : 'text-zinc-300'} />
                  <span className={`text-sm font-semibold tracking-tight ${
                    chosenTrial ? 'text-cyan-300' : 'text-zinc-100'
                  }`}>7-Day Free Trial</span>
                </span>
                <span className={`block text-xs mt-1 ${
                  chosenTrial ? 'text-cyan-200/80' : 'text-zinc-300'
                }`}>$0 today, then {planPriceLabel}</span>
              </button>
              <button
                onClick={() => setChosenTrial(false)}
                disabled={loading}
                className={`relative px-4 py-3.5 rounded-lg border text-left transition-all duration-150 ${
                  !chosenTrial
                    ? 'bg-cyan-500/[0.08] border-cyan-400/50 shadow-lg shadow-cyan-500/10'
                    : 'bg-zinc-900/30 border-zinc-800/60 hover:border-zinc-600'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <CreditCard size={14} className={!chosenTrial ? 'text-cyan-300' : 'text-zinc-300'} />
                  <span className={`text-sm font-semibold tracking-tight ${
                    !chosenTrial ? 'text-cyan-300' : 'text-zinc-100'
                  }`}>Subscribe Today</span>
                </span>
                <span className={`block text-xs mt-1 ${
                  !chosenTrial ? 'text-cyan-200/80' : 'text-zinc-300'
                }`}>{planPriceLabel} billed now</span>
              </button>
            </div>
          )}

          {/* Name field */}
          <div>
            <label className="block text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-2">
              Full Legal Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={loading}
              placeholder="As it appears on your government-issued ID"
              className="w-full px-4 py-3 bg-zinc-900/50 border border-zinc-800/60 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-cyan-500/60 transition-all disabled:opacity-50"
            />
            {!nameValid && fullName.length > 0 && (
              <p className="text-xs text-red-400/80 mt-1.5">Please enter your full name</p>
            )}
          </div>

          {/* ---- Simplified Data Access Verification ---- */}
          <div className="rounded-lg border border-zinc-800/60 overflow-hidden">
            {/* Top bar */}
            <div className="bg-zinc-900/80 px-4 py-3 flex items-center gap-2.5 border-b border-zinc-800/40">
              <Lock size={14} className="text-cyan-400/70" />
              <span className="text-xs font-semibold text-zinc-100 tracking-tight">
                Data Access Verification
              </span>
            </div>

            <div className="px-4 py-3.5">
              <p className="text-xs text-zinc-300 leading-relaxed">
                Xynth provides <span className="text-white font-medium">real-time market data from NYSE, NASDAQ, and CBOE</span>. 
                Exchange regulations require us to verify your subscriber type.
              </p>

              <div className="mt-3 pt-3 border-t border-zinc-800/40">
                <p className="text-xs font-medium text-zinc-200 mb-2">
                  Select Non-Professional if you:
                </p>
                <ul className="space-y-1.5 text-xs text-zinc-300">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    <span>Trade for yourself (not a business or institution)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    <span>Are not a registered broker, advisor, or fund manager</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    <span>Use data for personal investment decisions only</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Status buttons inside the card */}
            <div className="px-4 pb-4 grid grid-cols-2 gap-3">
              <button
                onClick={() => setStatus('non-professional')}
                disabled={loading}
                className={`relative px-4 py-3 rounded-lg border text-left transition-all duration-150 ${
                  status === 'non-professional'
                    ? 'bg-emerald-500/[0.06] border-emerald-500/30'
                    : 'bg-zinc-900/30 border-zinc-800/60 hover:border-zinc-600'
                }`}
              >
                <span className={`block text-sm font-semibold tracking-tight ${
                  status === 'non-professional' ? 'text-emerald-400' : 'text-zinc-100'
                }`}>Non-Professional</span>
                <span className={`block text-xs mt-0.5 ${
                  status === 'non-professional' ? 'text-emerald-400/70' : 'text-zinc-300'
                }`}>Personal use</span>
              </button>
              <button
                onClick={() => setStatus('professional')}
                disabled={loading}
                className={`relative px-4 py-3 rounded-lg border text-left transition-all duration-150 ${
                  status === 'professional'
                    ? 'bg-zinc-100/[0.04] border-zinc-500/30'
                    : 'bg-zinc-900/30 border-zinc-800/60 hover:border-zinc-600'
                }`}
              >
                <span className={`block text-sm font-semibold tracking-tight ${
                  status === 'professional' ? 'text-white' : 'text-zinc-100'
                }`}>Professional</span>
                <span className={`block text-xs mt-0.5 ${
                  status === 'professional' ? 'text-zinc-200' : 'text-zinc-300'
                }`}>Business / institutional</span>
              </button>
            </div>
          </div>

          {/* Professional to Elite redirect */}
          {isProfessional && !isElitePlan && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04] px-4 py-3.5 animate-in fade-in-0 slide-in-from-top-1 duration-200">
              <p className="text-xs text-zinc-200 leading-relaxed">
                Professional subscribers require the <span className="text-amber-300 font-semibold">Elite plan</span> for
                exchange-compliant data licensing, unlimited AI messages, and full institutional-grade access.
              </p>
            </div>
          )}

          {/* Terms checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              disabled={loading}
              className="mt-1 h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-cyan-400 focus:ring-cyan-600 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-xs text-zinc-200 group-hover:text-white transition-colors leading-relaxed">
              <FileText size={12} className="inline mr-1.5 -mt-0.5 opacity-60" />
              I agree to the{' '}
              <a href="/terms" target="_blank" className="text-white hover:text-cyan-300 underline underline-offset-2 font-medium">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" className="text-white hover:text-cyan-300 underline underline-offset-2 font-medium">
                Privacy Policy
              </a>
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-zinc-800/40 flex items-center justify-end gap-3 bg-zinc-950">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-xs font-medium text-zinc-300 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>

          {isProfessional && !isElitePlan ? (
            <button
              onClick={onSwitchToElite}
              disabled={!nameValid || !termsAccepted}
              className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                nameValid && termsAccepted
                  ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-zinc-950 hover:from-amber-300 hover:to-amber-400 hover:shadow-lg hover:shadow-amber-500/20'
                  : 'bg-zinc-900 border border-zinc-800/60 text-zinc-500 cursor-not-allowed'
              }`}
            >
              Switch to Elite
            </button>
          ) : (
            <button
              onClick={() => onConfirm(status as 'non-professional' | 'professional', fullName.trim(), effectiveTrial)}
              disabled={!canProceed}
              className={`relative px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-200 min-w-[180px] ${
                canProceed
                  ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-zinc-950 hover:from-cyan-300 hover:to-cyan-400 hover:shadow-lg hover:shadow-cyan-500/20'
                  : 'bg-zinc-900 border border-zinc-800/60 text-zinc-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span>Processing...</span>
                </span>
              ) : effectiveTrial ? (
                'Start Free Trial'
              ) : (
                'Continue to Checkout'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
